import { memo, useState } from 'react';
import styled from '@emotion/styled';
import { Grid, Typography } from '@mui/material';
import TitleBrowserAccordion from '@/components/OrderPlacement/OPStep2FileSelection/TitleBrowser/TitleBrowserAccordion';
import { VersionGroupedByParent } from '@/redux/slices/slice_types';
import BulkResultVersionEntity from './BulkResultVersionEntity';
import VersionSelectionPopout from './VersionSelectionPopout';
import { getVersionFromGroup } from '@/_utils/bulkSearchUtils';

type TitleBrowserItemV2Props = {
  data: VersionGroupedByParent;
};

const sortSeasons = (data: VersionGroupedByParent) => {
  const seasonKeys = Object.keys(data);
  return seasonKeys.sort((a, b) => {
    const seasonA = getVersionFromGroup(data, a);
    const seasonB = getVersionFromGroup(data, b);
    return Number(seasonA?.lineage_meta?.season_sequence_order) - Number(seasonB?.lineage_meta?.season_sequence_order);
  });
}

const BulkResultSeries = ({ data }: TitleBrowserItemV2Props) => {
  const seasonKeys = sortSeasons(data);
  const [expanded, setExpanded] = useState(false);

  if (seasonKeys.length === 0) {
    return null;
  }


  return seasonKeys.map(seasonKey => {
    const version = getVersionFromGroup(data, seasonKey);
    const episodeKeys = Object.keys(data[seasonKey]).sort((e1, e2) => {
      const episode1 = getVersionFromGroup(data[seasonKey], e1);
      const episode2 = getVersionFromGroup(data[seasonKey], e2);
      return Number(episode1?.lineage_meta?.episode_sequence_order) - Number(episode2?.lineage_meta?.episode_sequence_order);
    });
    return (
    <TitleBrowserAccordion
      key={seasonKey}
      expandedCallback={setExpanded}
      title={
        <TitleRow>
          <Grid container alignItems="center">
            <Grid item component={Typography} sx={{ ml: 1 }}>
              {version?.lineage_meta?.season_name}
            </Grid>

            <Grid item display="flex" flexGrow={1}>
              <Grid item display="flex" alignItems="center" sx={{ opacity: 0.5 }}>
                <span style={{ margin: '0 10px' }}>|</span>
                <Typography>
                  {episodeKeys.length} episode
                  {episodeKeys.length !== 1 ? 's' : ''}
                </Typography>
              </Grid>
            </Grid>
            {!expanded && <VersionSelectionPopout data={data[seasonKey]} />}
          </Grid>
        </TitleRow>
      }
    >
      {episodeKeys.map((episodeKey) => (
        <BulkResultVersionEntity contrastNested key={episodeKey} entity={data[seasonKey][episodeKey]} />
      ))}
    </TitleBrowserAccordion>
    );
    }
  );
};

export default memo(BulkResultSeries);

const TitleRow = styled.div(({ theme }) => ({
  justifyContent: 'flex-start',
  width: '100%',
  padding: theme.spacing(1.5, 2, 1.5, 0),
  color: theme.palette.text.primary,
  textTransform: 'none',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  flexDirection: 'row',
  marginRight: 10,

  '.MuiTypography-root, span': {
    fontSize: '1.2rem',
    whiteSpace: 'nowrap'
  }
}));